// ==========================================================================
// StarPlayer Agent
// ==========================================================================

import support from '../support';
import { toggleClass, removeElement, createElement } from '../utils/elements';
import { on, once, triggerEvent } from '../utils/events';
import is from '../utils/is';
import { silencePromise } from '../utils/promise';
import { setAspectRatio } from '../utils/style';
import { format } from '../utils/strings';
import i18n from '../utils/i18n';
import browser from '../utils/browser';
import { replaceAll } from '../utils/strings';
import ui from '../ui';
import { fetch as fetchPolyfill } from 'whatwg-fetch'
import { parseUrl } from '../utils/urls';
import Tracker from './tracker';
import { getDate } from '../utils/time';
// import { fetchToken } from '../utils/fetch';

function getAgentSetupUrl() {
  if (browser.isOsx) {
    return STARPLAYER_OSX_SETUP_URL;
  }
  return STARPLAYER_AGENT_SETUP_URL;
}

function getAgentMinimumVersion() {
  if (browser.isOsx) {
    return STARPLAYER_OSX_VERSION;
  }
  return STARPLAYER_AGENT_VERSION;
}

function getVodUrl(url, sid) {
  let newUrl;

  let sep = '?';
  if (url.indexOf('?') != -1) {
    sep = '&';
  }

  if (window.location.protocol == 'https:') {
    newUrl = 'https://localhost.axissoft.co.kr:3454/{0}' + sep + 'sid={1}';
  } else {
    newUrl = 'http://127.0.0.1:3450/{0}' + sep + 'sid={1}';
  }

  return format(newUrl, url, sid);
}

function getWSUrl() {
  let url;
  if (window.location.protocol == 'https:') {
    url = 'wss://localhost.axissoft.co.kr:3453/starplayer';
  } else {
    url = 'ws://127.0.0.1:3451/starplayer';
  }
  return url;
}

/** 에이전트가 보낸 확장 목록을 문자열 id 배열로 통일 */
function normalizeBrowserExtensionIds(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = [];
  for (let i = 0; i < raw.length; i++) {
    const item = raw[i];
    if (typeof item === 'string' && item) {
      out.push(item);
    } else if (item && typeof item === 'object' && typeof item.id === 'string' && item.id) {
      out.push(item.id);
    }
  }
  return out;
}

function checkAgentVersion(agentVersion, seperator) {
  const wantedVersion = getAgentMinimumVersion().split(',');
  const currentVersion = agentVersion.split(seperator);
  function check(i) {
    const cv = parseInt(currentVersion[i], 10);
    const wv = parseInt(wantedVersion[i], 10);
    if (cv > wv) {
      return 1;
    } else if (cv == wv) {
      if (i == 3) return 0;
      else return check(i + 1);
    } else {
      return -1;
    }
  }
  return check(0);
}

function getLearningRecordDateStyle(date) {
  const format = (value) => `0${value}`.slice(-2);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const mins = date.getMinutes();
  const secs = date.getSeconds();

  return `${year}/${format(month)}/${format(day)} ${format(hours)}:${format(mins)}:${format(secs)}`;
}


function getEventDateStyle(date) {
  const format = (value) => `0${value}`.slice(-2);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const mins = date.getMinutes();
  const secs = date.getSeconds();

  return `${year}${format(month)}${format(day)}${format(hours)}${format(mins)}${format(secs)}`;
}

async function waitForWebSocketClosure(websocket) {
  if (websocket.readyState === WebSocket.CLOSED) {
    return;
  }
  return new Promise((resolve) => {
    websocket.addEventListener('close', () => {
      resolve();
    });
  });
}

function getAbsoluteURL(url) {
  // Check if absolute URL
  if (!url.match(/^https?:\/\//)) {
    const div = document.createElement('div')
    div.innerHTML = `<a href="${url}">x</a>`
    url = div.firstChild.href
  }
  return url
}

const starplayer = {

  showMessage(message, passive = true) {
    if (passive && this.message) {
      return Promise.reject(new Error('message already display'));
    }

    if (this.elements.message) {
      this.message = true;

      ui.togglePoster.call(this, false);

      this.elements.message.removeAttribute('hidden');
      this.elements.text.innerHTML = message;
      toggleClass(this.elements.container, this.config.classNames.messageEnabled, true);
    }
  },

  getSources() {
    if (!this.isStarPlayer) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('sourcex'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');

      if (is.empty(type)) {
        return true;
      }

      return support.mime.call(this, type);
    });
  },

  // Get quality levels
  getQualityOptions() {
    // Whether we're forcing all options (e.g. for streaming)
    if (this.config.quality.forced) {
      return this.config.quality.options;
    }

    // Get sizes from <source> elements
    return starplayer.getSources
      .call(this)
      .map((source) => Number(source.getAttribute('size')))
      .filter(Boolean);
  },

  initPlayingEvent() {
    const player = this;
    if (player.source == getAbsoluteURL(this.config.blankVideo)) {
      player.debug.log(player.source);
      return;
    }

    if (player.stars.sentUnloadEvent == true) {
      return;
    }

    player.debug.log("initPlayingEvent");

    clearInterval(player.stars.playingContentEventTimer);

    if (player.stars.tick == 0) {
      player.stars.tick = new Date().getTime();
    }
    player.stars.playingContentEventTimer = setInterval(() => {
      starplayer.ContentEvent.call(player, 'playing_content', (txt) => {
        player.playingContentEvent(txt);
      });
    }, 180000);
  },

  setup() {
    if (!this.isStarPlayer) {
      return;
    }

    const player = this;

    if (player.dash && browser.isSafari) {
      starplayer.showMessage.call(player, i18n.get('canNotSupportSafari', player.config.starplayer));
      return;
    }

    // if (browser.isFirefox) {
    //   starplayer.cancelRequests.call(player);
    //   starplayer.showMessage.call(player, i18n.get('canNotSupportSafari', player.config.starplayer));
    //   return;
    // }

    player.stars.tick = 0;
    player.stars.playedTimeWithSpeed = 0;
    player.stars.canceled = false;
    player.stars.sentUnloadEvent = false;
    player.message = false;

    on.call(player, player.media, 'error', () => {
      if (!player.media.error) return;
      if (player.source === getAbsoluteURL(player.config.blankVideo)) return;
      if (player.message) return;

      let msg;
      switch (player.media.error.code) {
        case 1: // MEDIA_ERR_ABORTED:
          msg = i18n.get('mediaErrorAbort', player.config.starplayer);
          break;
        case 2: // MEDIA_ERR_NETWORK:
          msg = i18n.get('mediaErrorNetwork', player.config.starplayer);
          break;
        case 3: // MEDIA_ERR_DECODE:
          msg = i18n.get('mediaErrorDecode', player.config.starplayer);
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED:
          msg = i18n.get('mediaErrorSrcNotSupported', player.config.starplayer);
          break;
        default:
          msg = i18n.get('mediaErrorUnknown', player.config.starplayer);
          break;
      }
      starplayer.cancelRequests.call(player);
      starplayer.showMessage.call(player, msg);
    });

    // on.call(player, player.media, 'loadstart', () => {
    //     //OPENING
    // });

    if (starplayer.watermarkEnabled.call(player)) {
      on.call(player, player.media, 'playing', () => {
        if (!is.element(player.elements.watermark)) {
          starplayer.watermarkRender.call(player);
        }
      });

      on.call(player, player.media, 'ended emptied abort error', () => {
        starplayer.watermarkRelease.call(player);
      });
    }

    on.call(player, player.media, 'ended abort', (event) => {
      // player.debug.log(event.type)
      const userId = player.config.starplayer.userId;
      if (is.url(player.config.starplayer.logUrl) && userId != 'ANONYMOUS') {
        clearInterval(player.stars.learningRecordTimer);
        if (player.tracker && player.duration != 0 && player.tracker.getPlayedTime() != 0) {
          starplayer.scmsDisconnected.call(player);
        }
      }
    });
    
    // function initPlayingEvent() {
    //   const player = this;
    //   if (player.source == getAbsoluteURL(this.config.blankVideo)) {
    //     player.debug.log(player.source);
    //     return;
    //   }

    //   if (player.stars.sentUnloadEvent == true) {
    //     return;
    //   }

    //   player.debug.log("initPlayingEvent");

    //   clearInterval(player.stars.playingContentEventTimer);

    //   if (player.stars.tick == 0) {
    //     player.stars.tick = new Date().getTime();
    //   }
    //   player.stars.playingContentEventTimer = setInterval(() => {
    //     starplayer.ContentEvent.call(player, 'playing_content', (txt) => {
    //       player.playingContentEvent(txt);
    //     });
    //   }, 180000);
    // }

    // once.call(player, player.media, 'playing', () => {
    //   initPlayingEvent.call(this);
    // });
    
    on.call(player, player.media, 'play', () => {
      starplayer.initPlayingEvent.call(this);
    });

    window.addEventListener('pagehide', (e)=> {
      player.debug.log('>==pagehide', player.stars.sentUnloadEvent);

      if (player.stars.sentUnloadEvent == true) {
        return;
      }
      
      if (player.source == getAbsoluteURL(this.config.blankVideo)) {
        player.debug.log(player.source);
        return;
      }
      clearInterval(player.stars.playingContentEventTimer);
      starplayer.ContentEvent.call(player, 'end_content', (txt)=> {
        player.endContentEvent(txt);
      });

      return;
    })

    on.call(player, player.media, 'opened', (event) => {

      on.call(player, player.media, 'pause', () => {
        if (player.source == getAbsoluteURL(this.config.blankVideo)) {
          player.debug.log(player.source);
          return;
        }
        clearInterval(player.stars.playingContentEventTimer);
        starplayer.ContentEvent.call(player, 'playing_content', (txt) => {
          player.playingContentEvent(txt);
          player.stars.tick = 0;
        });
      });

      const status = event.detail.status;
      if (status == 'UnknownError') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('mediaErrorUnknown', player.config.starplayer));
      }

      clearInterval(player.stars.keepAliveTimer);
      player.stars.keepAliveTimer = setInterval(() => {
        starplayer.scmsKeepAlive.call(player);
      }, 1000 * player.stars.keepaliveInterval);

      clearInterval(player.stars.emergencyTimer);
      player.stars.emergencyTimer = setInterval(()=> {
        starplayer.emergency.call(player, +player.playing);
      }, 5000);

      const userId = player.config.starplayer.userId;
      if (is.url(player.config.starplayer.logUrl) && userId != 'ANONYMOUS') {
        // player.tracker = new Tracker(player);
        clearInterval(player.stars.learningRecordTimer);
        player.stars.learningRecordTimer = setInterval(() => {
          if (player.tracker && player.duration != 0 && player.tracker.getPlayedTime() != 0) {
            starplayer.scmsSaveRecordInfo.call(player);
          }
        }, 1000);
      }

      if (browser.isChrome || browser.isEdge || browser.isWhale || browser.isFirefox) {
        clearInterval(player.stars.browserExtensionTimer);
        const runBrowserExtensionCheck = () => {
          starplayer.runBrowserExtensionPoll.call(player);
        };
        runBrowserExtensionCheck();
        player.stars.browserExtensionTimer = setInterval(runBrowserExtensionCheck, 1000 * 10);
      }

      starplayer.beginContentEvent.call(player);

      on.call(player, player.media, 'ended emptied abort error', () => {
        if (player.stars.sentUnloadEvent == true) {
          return;
        }

        if (player.source == getAbsoluteURL(this.config.blankVideo)) {
          player.debug.log(player.source);
          return;
        }
        clearInterval(player.stars.playingContentEventTimer);
        starplayer.ContentEvent.call(player, 'end_content', (txt)=> {
          player.endContentEvent(txt);
        });
      });  

    });

    on.call(player, player.media, 'keepalive', (event) => {
      const status = event.detail.status;
      if (status == 'MultipleConnections') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('multipleConnections', player.config.starplayer));
      } else if (status == 'DetectHardware') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('detectedHardware', player.config.starplayer));
      } else if (status == 'CanNotTerminated') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('detectedProcessHidder', player.config.starplayer));
      }
    });

    // Set speed options from config
    player.options.speed = player.config.speed.options; //[0.6, 0.8, 1, 1.2, 1.4, 1.6, 1.8, 2.0]

    // Set aspect ratio if fixed
    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }

    // Quality
    Object.defineProperty(player.media, 'quality', {
      get() {
        // Get sources
        const sources = starplayer.getSources.call(player);
        const currentSrc = player.source;
        const source = sources.find((s) => currentSrc.indexOf(s.getAttribute('src')) != -1);

        // Return size, if match is found
        return source && Number(source.getAttribute('size'));
      },
      set(input) {
        // forced + onChange는 starplyr.js set quality에서 직접 호출하므로 여기서는 skip
        if (player.config.quality.forced && is.function(player.config.quality.onChange)) {
          // onChange는 이미 호출됨 — qualitychange 이벤트만 발생
        } else if (player.quality === input && !is.empty(player.media.src)) {
          return;
        } else {
          // Get sources
          const sources = starplayer.getSources.call(player);
          // Get first match for requested size
          const source = sources.find((s) => Number(s.getAttribute('size')) === input);

          // No matching source found
          if (!source) {
            return;
          }

          player.elements.inputs.seek.disabled = true;
          // Get current state
          const { currentTime, paused, preload, readyState, playbackRate } = player.media;

          player.stars.contentId = source.getAttribute('contentid') || '';

          // Set new source
          const src = source.getAttribute('src');

          once.call(player, player.media, 'connection', (e) => {

            player.stars.beginLocaltime = getLearningRecordDateStyle(new Date());

            const status = e.detail.status;
            const msg = e.detail.msg;
            const sessionId = e.detail.session_id;

            if (!starplayer.checkStatus.call(player, status, msg)) {
              return;
            }

            player.stars.currentUrl = src;
            const newUrl = getVodUrl(src, sessionId);
            player.media.src = newUrl;

            // Prevent loading if preload="none" and the current source isn't loaded (#1044)
            if (preload !== 'none' || readyState) {
              // Restore time
              player.once('loadedmetadata', () => {
                // player.speed = playbackRate;
                player.elements.inputs.seek.disabled = false;
                player.speed = null;
                player.currentTime = currentTime;

                starplayer.scmsOpened.call(player);
                // Resume playing
                if (!paused) {
                  silencePromise(player.play());
                }
              });

              // Load new source
              player.media.load();
            }
          });

          starplayer.wakeupAgent.call(player, src)
        }

        // Trigger change event
        triggerEvent.call(player, player.media, 'qualitychange', false, {
          quality: input,
        });
      },
    });

    //for dash
    if (!player.stars.currentUrl && player.source.indexOf('blob:', 0) == 0) {

      setTimeout(() => {

        let src = player.source;
        let sourceElement = player.media.querySelector('source');
        if (is.element(sourceElement)) {
          src = sourceElement.getAttribute('src');
        } else {
          sourceElement = player.media.querySelector('sourcex');
          if (is.element(sourceElement)) {
            src = sourceElement.getAttribute('src');
          }
        }

        player.stars.contentId = sourceElement.getAttribute('contentid') || '';

        once.call(player, player.media, 'connection', (e) => {

          player.stars.beginLocaltime = getLearningRecordDateStyle(new Date());

          const status = e.detail.status;
          const msg = e.detail.msg;
          // const sessionId = e.detail.session_id;

          if (!starplayer.checkStatus.call(player, status, msg)) {
            return;
          }

          player.stars.currentUrl = src;
          player.elements.inputs.seek.disabled = false;
          player.speed = null;
          starplayer.scmsOpened.call(player);
        });

        starplayer.wakeupAgent.call(player, src);
        
      }, 2000);
    } else {
      if (player.hls) {
        starplayer.scmsOpened.call(player);
      }
    }
  },

  getNewHostUrl(url) {
    const player = this;

    if (is.array(url)) {
      const [{src},] = url;
      url = src;
    }

    return new Promise((resolve, reject) => {

      once.call(player, player.media, 'connection', (e) => {

        player.stars.beginLocaltime = getLearningRecordDateStyle(new Date());

        const status = e.detail.status;
        const msg = e.detail.msg;
        const sessionId = e.detail.session_id;
        const newUrl = getVodUrl(url, sessionId);

        if (!starplayer.checkStatus.call(player, status, msg)) {
          return reject();
        }

        player.stars.currentUrl = url;
        player.speed = null;
        // starplayer.scmsOpened.call(player);
        resolve(newUrl);
      });


      starplayer.connectAgent
          .call(this)
          .then(() => {
            let src = player.source;
            let sourceElement = player.media.querySelector('source');
            if (is.element(sourceElement)) {
              src = sourceElement.getAttribute('src');
            } else {
              sourceElement = player.media.querySelector('sourcex');
              if (is.element(sourceElement)) {
                src = sourceElement.getAttribute('src');
              }
            }

            player.stars.contentId = sourceElement.getAttribute('contentid') || '';

            

            starplayer.wakeupAgent.call(player, src);
          })
    });
  },

  checkStatus(status, msg) {
    if (status != '' && status != 'Connected') {
      if (msg !== '') {
        // player.debug.log(msg);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, msg);
        return false;
      }
      if (status == 'MultipleConnections') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('multipleConnections', player.config.starplayer));
      } else if (status == 'BlockedUID') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedUserId', player.config.starplayer));
      } else if (status == 'BlockedIP') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedIp', player.config.starplayer));
      } else if (status == 'BlockedPID') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedMachine', player.config.starplayer));
      } else if (status == 'VMACHINE') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedVmachine', player.config.starplayer));
      } else if (status == 'RDESKTOP') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedRdesktop', player.config.starplayer));
      }
      return false;
    }
    return true;
  },

  wakeupAgent(src) {
    const player = this;
    if (player.config.starplayer.token.enabled) {
      const option = {
        url: player.config.starplayer.token.url,
        apiKey: player.config.starplayer.token.apiKey,
        vod: parseUrl(src).pathname,
      };

      // fetchToken(option).then((json)=>{
      //   if (!is.empty(json.api_data)) {
      //     starplayer.sendUrl.call(player, src, json.api_data);
      //     starplayer.scmsConnection.call(player);
      //   } else {
      //     starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));  
      //   }
      // }).catch((e)=>{
      //   starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
      // });

      fetchPolyfill(option.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api_key': option.apiKey,
        },
        body: JSON.stringify({ vod: option.vod, })
      }).then((response) => {
        return response.json();
      }).then((json) => {
        if (!is.empty(json.api_data)) {
          clearInterval(player.stars.learningRecordTimer);
          starplayer.sendUrl.call(player, src, json.api_data);
          starplayer.scmsConnection.call(player);
        } else {
          starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
        }
      }).catch((e) => {
        starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
      });

    } else {
      clearInterval(player.stars.learningRecordTimer);
      starplayer.sendUrl.call(player, src);
      starplayer.scmsConnection.call(player);
    }
  },

  recordInfo() {
    const player = this;

    const endLocaltime = getLearningRecordDateStyle(new Date());
    const userId = unescape(encodeURIComponent(encodeURIComponent(player.config.starplayer.userId)));
    const extra = unescape(encodeURIComponent(encodeURIComponent(player.stars.contentId)));

    const info = `user_id=${userId}&player_id=${player.stars.playerId}&content_duration=${parseInt(player.duration, 10)}&current_position=${parseInt(player.currentTime, 10)}&played_time=${player.tracker.getPlayedTime()}&strong_played_time=${player.tracker.getPlayedTimeWithSpeed()}&tracker_time=${player.tracker.getTrackerTime()}&tracker_data=${player.tracker.getTracker()}&extra_data=${extra}&begin_localtime=${encodeURIComponent(player.stars.beginLocaltime)}&end_localtime=${encodeURIComponent(endLocaltime)}&content_url=${player.stars.currentUrl}`;
    // player.debug.log(info);
    return info;
  },

  sendUrl(src, token = null) {
    const player = this;
    var extra = unescape(encodeURIComponent(encodeURIComponent(player.stars.contentId)));
    const msg = {
      url: src,
      custom_log_url: player.config.starplayer.logUrl,
      custom_log_extra: extra,
      url_token: token,
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsConnection() {
    const player = this;
    const msg = {
      scms: 'connection',
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsOpened() {
    const player = this;
    const msg = {
      scms: 'opened',
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsKeepAlive() {
    const player = this;
    const msg = {
      scms: 'keepalive',
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsDisconnected() {
    const player = this;
    const info = starplayer.recordInfo.call(player);
    const msg = {
      disconnected: window.btoa(info),
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsSaveRecordInfo() {
    const player = this;
    const info = starplayer.recordInfo.call(player);
    const msg = {
      history: window.btoa(info),
    }
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsSaveRecordNullInfo() {
    const player = this;
    const userId = unescape(encodeURIComponent(encodeURIComponent(player.config.starplayer.userId)));
    const msg = {
      history: window.btoa(`user_id=${userId}&player_id=${player.stars.playerId}`),
    }
    player.stars.ws.send(JSON.stringify(msg));
  },

  emergency(playstate) {
    const player = this;
    const msg = {
      emergency: playstate, //playing 1
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  resolveBrowserExtensionChannelName() {
    if (browser.isChrome) {
      return 'chrome';
    }
    if (browser.isEdge) {
      return 'edge';
    }
    if (browser.isWhale) {
      return 'whale';
    }
    if (browser.isFirefox) {
      return 'firefox';
    }
    return null;
  },

  /** 목록 미수신 시 1회 열거 요청, 수신 후에는 미체크 확장만 전송 */
  runBrowserExtensionPoll() {
    const player = this;
    const browserName = starplayer.resolveBrowserExtensionChannelName();
    if (!browserName) {
      return;
    }
    if (!player.stars.ws || player.stars.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    const ids = player.stars.browserExtensionIdList;
    if (ids === null || typeof ids === 'undefined') {
      if (!player.stars.browserExtensionDiscoverySent) {
        player.stars.ws.send(JSON.stringify({ browser_extension: browserName }));
        player.stars.browserExtensionDiscoverySent = true;
      }
      return;
    }
    starplayer.syncBrowserExtensionChecks.call(player, browserName);
  },

  /** 에이전트가 준 확장 id 중 아직 전송 안 한 것만 checkBrowserExtension */
  syncBrowserExtensionChecks(browserName) {
    const player = this;
    const ids = player.stars.browserExtensionIdList;
    if (!ids || !ids.length) {
      return;
    }
    if (!player.stars.browserExtensionIdsChecked) {
      player.stars.browserExtensionIdsChecked = new Set();
    }
    if (!player.stars.ws || player.stars.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    for (let i = 0; i < ids.length; i++) {
      const extId = ids[i];
      if (!player.stars.browserExtensionIdsChecked.has(extId)) {
        starplayer.checkBrowserExtension.call(player, browserName, extId);
      }
    }
  },

  // browser: chrome, edge, firefox + extension_id (플레이어 인스턴스당 확장 id별 1회)
  checkBrowserExtension(browserName, extensionId) {
    const player = this;
    if (!extensionId) {
      return;
    }
    if (!player.stars.browserExtensionIdsChecked) {
      player.stars.browserExtensionIdsChecked = new Set();
    }
    if (player.stars.browserExtensionIdsChecked.has(extensionId)) {
      return;
    }
    if (!player.stars.ws || player.stars.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    const msg = {
      browser_extension: browserName,
      extension_id: extensionId,
    };
    player.stars.ws.send(JSON.stringify(msg));
    player.stars.browserExtensionIdsChecked.add(extensionId);
  },

  installAgent() {
    const player = this;

    let msg = i18n.get('installAgent', player.config.starplayer);
    const url = getAgentSetupUrl();
    msg = replaceAll(msg, '{agentSetupUrl}', url);
    starplayer.showMessage.call(player, msg);
    player.stars.installTimer = setTimeout(() => {
      starplayer.checkInstalled.call(player);
    }, 500);
  },

  checkInstalled() {
    if (!this.isStarPlayer && !this.isYoutubeStarPlayer) {
      return;
    }
    const player = this;
    player.stars.ws2 = window.StarPlayerNative
      ? new window.StarPlayerNative.NativeChannel()
      : new WebSocket(getWSUrl());
    const ws = player.stars.ws2;
    // NativeChannel.send는 OPEN이 아니면 throw → 가드 없는 send가 UI를 깨뜨림. WebSocket처럼 무시하도록 래핑.
    if (ws && ws.constructor && ws.constructor.name === 'NativeChannel') {
      const _send = ws.send.bind(ws);
      ws.send = (d) => { try { if (ws.readyState === 1) _send(d); } catch (_) {} };
    }
    ws.onopen = (evt) => {
      const msg = {
        config: player.config.starplayer.config,
      };
      ws.send(JSON.stringify(msg));
    };
    ws.onmessage = (evt) => {
      const jsonObj = JSON.parse(evt.data);
      if (typeof jsonObj.version !== 'undefined') {
        if (checkAgentVersion(jsonObj.version, '.') == -1) {
          player.stars.installTimer = setTimeout(() => {
            starplayer.checkInstalled.call(player);
          }, 1000);
        } else {
          setTimeout(() => {
            location.reload();
          }, 100);
        }
      }
      ws.close();
    };
    ws.onclose = (evt) => {
      player.stars.installTimer = setTimeout(() => {
        starplayer.checkInstalled.call(player);
      }, 500);
    };
    ws.onerror = (evt) => { };
  },

  connectAgent() {

    const player = this;

    player.stars.keepaliveInterval = 60;

    return new Promise((resolve, reject) => {

      if (player.stars.ws) {
        resolve();
        return;
      }

      player.stars.isStarPlayerInstalled = false;
      player.stars.ws = window.StarPlayerNative
        ? new window.StarPlayerNative.NativeChannel()
        : new WebSocket(getWSUrl());
      player.debug.log('starplayer ws new');
      const ws = player.stars.ws;
      // NativeChannel.send는 OPEN이 아니면 throw → 가드 없는 send가 UI를 깨뜨림. WebSocket처럼 무시하도록 래핑.
      if (ws && ws.constructor && ws.constructor.name === 'NativeChannel') {
        const _send = ws.send.bind(ws);
        ws.send = (d) => { try { if (ws.readyState === 1) _send(d); } catch (_) {} };
      }
      ws.onopen = (evt) => {
        clearTimeout(player.stars.canceledTimer);

        let referer;
        try {
          referer = window.parent.location.href;
        } catch (_) {
          referer = window.location.href;
        }
        const msg = {
          //url: player.source,
          config: player.config.starplayer.config,
          referer,
          spk_id: player.config.starplayer.spkId,
          user_id: player.config.starplayer.userId,
        };
        ws.send(JSON.stringify(msg));
      };
      ws.onmessage = (evt) => {
        const jsonObj = JSON.parse(evt.data);
        if (
          typeof jsonObj.browser_extensions !== 'undefined' ||
          jsonObj.method == 'browser_extensions'
        ) {
          const raw =
            jsonObj.browser_extensions !== undefined
              ? jsonObj.browser_extensions
              : jsonObj.ids || jsonObj.extensions;
          const ids = normalizeBrowserExtensionIds(raw);
          player.stars.browserExtensionIdList = ids;
          const browserName = starplayer.resolveBrowserExtensionChannelName();
          if (browserName) {
            starplayer.syncBrowserExtensionChecks.call(player, browserName);
          }
        }
        if (typeof jsonObj.version !== 'undefined') {
          player.stars.isStarPlayerInstalled = true;
          if (checkAgentVersion(jsonObj.version, '.') == -1) {
            starplayer.installAgent.call(player);
            reject();
          } else {

            player.stars.playerId = jsonObj.player_id;
            player.stars.macAddress = jsonObj.mac_address;

            if (typeof jsonObj.keepalive_interval !== 'undefined') {
              player.stars.keepaliveInterval = jsonObj.keepalive_interval > 60 ? jsonObj.keepalive_interval : 60;
            }
            resolve();
          }
        }

        if (typeof jsonObj.method !== 'undefined') {
          if (jsonObj.method == 'keepalive') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              status: jsonObj.status,
            });
          } else if (jsonObj.method == 'connection') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              status: jsonObj.status,
              msg: decodeURIComponent(jsonObj.msg),
              session_id: jsonObj.session_id,
            });
          } else if (jsonObj.method == 'opened') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              status: jsonObj.status,
            });
          } else if (jsonObj.method == 'ssn') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              value: jsonObj.value,
            });
          } else if (jsonObj.method == 'signal') {
            if (is.url(player.config.starplayer.logUrl)) {
              const userId = player.config.starplayer.userId;
              if (jsonObj.status == 'stop' && jsonObj.value == player.config.starplayer.logUrl && userId != 'ANONYMOUS') {
                clearInterval(player.stars.learningRecordTimer);
                starplayer.scmsSaveRecordNullInfo.call(player);
                starplayer.cancelRequests.call(player);
                starplayer.showMessage.call(player, i18n.get('mediaErrorAbort', player.config.starplayer));
                return;
              }
            }

            if (jsonObj.status == 'stop' && jsonObj.value == 'DetectHardware') {
              starplayer.cancelRequests.call(player);
              starplayer.showMessage.call(player, i18n.get('detectedHardware', player.config.starplayer));
            } else if (jsonObj.status == 'stop' && jsonObj.value == 'CanNotTerminated') {
              starplayer.cancelRequests.call(player);
              starplayer.showMessage.call(player, i18n.get('detectedProcessHidder', player.config.starplayer));
            } else if (jsonObj.status == 'stop') {
              try {
                var obj = JSON.parse(jsonObj.value);
                starplayer.cancelRequests.call(player);
                starplayer.showMessage.call(
                  player,
                  i18n.get('detectedBrowserExtension', player.config.starplayer) +
                  '<br /> ( <a href=' +
                  obj.link +
                  ' target="_blank">' +
                  obj.module +
                  '</a> )',
                );
              } catch (_) {
              }
            }
          }
          // else if (jsonObj.method == 'chrome_extension') {
          //   triggerEvent.call(player, player.media, jsonObj.method, false, {
          //     id: jsonObj.id,
          //     installed: jsonObj.installed
          //   });
          // }
        }
      };
      ws.onclose = (evt) => {
        player.debug.log('starplayer ws onclose');

        if (!player.stars.isStarPlayerInstalled) {
          if (player.hls) {
            player.hls.destroy();
            player.hls = null;
          }
          if (player.dash) {
            player.dash.destroy();
            player.dash = null;
          }
          
          player.stars.canceledTimer = setTimeout(() => {
            starplayer.installAgent.call(player);
            reject();
          }, 1000);
        } else {
          if (!player.stars.canceled) {
            starplayer.cancelRequests.call(player);
            player.closedWebsocketEvent();

            if (player.hls) {
              player.hls.destroy();
              player.hls = null;
            }
            if (player.dash) {
              player.dash.destroy();
              player.dash = null;
            }
          }
          player.stars.canceled = false;
        }
      };
      ws.onerror = (evt) => {
        player.debug.log('starplayer onerror');
      };
    });
  },

  async cancelRequests() {
    if (!this.isStarPlayer) {
      return;
    }

    const player = this;
    clearTimeout(player.stars.installTimer);
    clearInterval(player.stars.keepAliveTimer);
    clearInterval(player.stars.emergencyTimer);
    clearInterval(player.stars.browserExtensionTimer);
    clearInterval(player.stars.learningRecordTimer);
    clearTimeout(player.stars.canceledTimer);
    clearInterval(player.stars.playingContentEventTimer);

    starplayer.watermarkRelease.call(player);

    // if (!this.message) {
    //   // if (this.elements.poster) {
    //   //   this.elements.poster.removeAttribute('hidden');
    //   // }
    //   ui.togglePoster.call(this, true);

    //   if (this.elements.message) {
    //     this.elements.message.setAttribute('hidden', '');
    //   }

    //   toggleClass(this.elements.container, this.config.classNames.messageEnabled, false);
    // }

    // Remove child sources
    removeElement(starplayer.getSources.call(this));

    if (player.hls) {
      player.hls.stopLoad(); 
    }

    // Set blank video src attribute
    // This is to prevent a MEDIA_ERR_SRC_NOT_SUPPORTED error
    // Info: http://stackoverflow.com/questions/32231579/how-to-properly-dispose-of-an-html5-video-and-close-socket-or-connection
    this.media.setAttribute('src', this.config.blankVideo);

    // Load the new empty source
    // This will cancel existing requests
    // See https://github.com/sampotts/plyr/issues/174
    this.media.load();

    if (player.stars.ws) {
      player.stars.canceled = true;
      player.stars.ws.close();
      await waitForWebSocketClosure(player.stars.ws);
      player.stars.ws = null;
    }

    // Debugging
    this.debug.log('Cancelled network requests');
  },

  watermarkEnabled() {
    const player = this;
    return (player.isStarPlayer || player.isYoutubeStarPlayer) && player.isVideo && player.config.starplayer.watermark.enabled;
  },

  setTimer(callback, interval) {
    if (interval <= 0) {
      return setTimeout(callback, 0);
    } else {
      return setInterval(callback, interval);
    }
  },

  clearTimer(timer, interval) {
    if (interval <= 0) {
      clearTimeout(timer);
    } else {
      clearInterval(timer);
    }
  },

  watermarkDisplay(displayDuration) {
    const player = this;
    if (player.elements.watermark == null) {
      return;
    }
    if (!player.elements.watermark.parentNode) {
      if (!is.element(player.elements.watermark)) {
        player.elements.watermark = createElement('div');
      }
      player.elements.wrapper.appendChild(player.elements.watermark);
    }

    player.elements.watermark.innerHTML = player.config.starplayer.watermark.text;
    let style = player.elements.watermark.style;
    style.position = 'absolute';
    style.width = '100%';
    style.display = 'block';
    style.margin = '0';
    style.padding = '0';
    style.textTransform = 'uppercase';
    style.fontWeight = 'bold';
    
    if (browser.isAndroid || browser.isIos) {
      style.fontSize = '100%';
    } else {
      style.fontSize = '150%';
    }
    style.color = player.config.starplayer.watermark.color;
    style.opacity = player.config.starplayer.watermark.opacity;
    style.filter = 'opacity(' + (style.opacity * 100) + '%)';
    style.zIndex = '1';

    let textAlign = 'initial';
    if (player.config.starplayer.watermark.horizontalAlign.toLowerCase() == 'random') {
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          textAlign = 'left';
          break;
        case 1:
          textAlign = 'center';
          break;
        case 2:
          textAlign = 'right';
          break;
      }
      style.textAlign = textAlign;
    } else {
      style.textAlign = player.config.starplayer.watermark.horizontalAlign.toLowerCase();
    }
    

    const rect = player.elements.container.getBoundingClientRect();
    let h;
    const middle = rect.height / 2;
    const mediaRatio = player.media.videoWidth / player.media.videoHeight;
    const clientRatio = rect.width / rect.height;
    if (mediaRatio >= clientRatio) {
      h = rect.width / mediaRatio / 2;
    } else {
      h = middle;
    }

    var watermarkHeight = player.elements.watermark.clientHeight;

    let top;
    if (player.config.starplayer.watermark.verticalAlign.toLowerCase() == 'random') {
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          top = middle - h;
          break;
        case 1:
          top = middle - (watermarkHeight / 2);
          break;
        case 2:
          top = middle + h - watermarkHeight;
          if (player.config.starplayer.watermark.displayTime) {
            player.elements.watermark.innerHTML = getDate(new Date());
          }
          break;
      }
    } else {
      switch (player.config.starplayer.watermark.verticalAlign.toLowerCase()) {
        case "top":
          top = middle - h;
          break;
        case "center":
          top = middle - (watermarkHeight / 2);
          break;
        case "bottom":
          top = middle + h - watermarkHeight;
          break;
      }
    }
    style.top = `${top}px`;

    player.stars.watermarkDurationTimer = setTimeout(() => {
      style.display = 'none';
    }, displayDuration * 1000 - 100);
  },

  watermarkRender() {
    const player = this;
    player.elements.watermark = createElement('div');
    player.elements.wrapper.appendChild(player.elements.watermark);

    let interval = player.config.starplayer.watermark.positionChangeInterval;
    // const intervalIsValid = is.number(interval) && interval > 0;
    // interval = intervalIsValid ? interval : 5;

    let displayDuration = player.config.starplayer.watermark.displayDuration;
    const displayDurationIsValid = is.number(displayDuration) && displayDuration < interval;
    displayDuration = displayDurationIsValid ? displayDuration : interval;
    if (interval <= 0) {
      displayDuration = 10800;
    }

    if (player.stars.watermarkTimer) {
      // clearInterval(player.stars.watermarkTimer);
      starplayer.clearTimer.call(player, player.stars.watermarkTimer, interval);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }

    starplayer.watermarkDisplay.call(player, displayDuration);

    if (interval > 0) {
      player.stars.watermarkTimer = setInterval(() => {
        starplayer.watermarkDisplay.call(player, displayDuration);
      }, interval * 1000);
    }
  },

  watermarkRelease() {
    const player = this;
    if (player.elements.watermark) {
      player.elements.watermark.remove();
      player.elements.watermark = null;
    }

    if (player.stars.watermarkTimer) {
      // clearInterval(player.stars.watermarkTimer);
      const interval = player.config.starplayer.watermark.positionChangeInterval;
      starplayer.clearTimer.call(player, player.stars.watermarkTimer, interval);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }
  },

  beginContentEvent() {
    this.debug.log('beginContentEvent event');
    const player = this;
    const userId = player.config.starplayer.userId;
    if (is.url(player.config.starplayer.eventUrl) && userId != 'ANONYMOUS') {
      let data = new FormData();
      data.append('event', "begin_content");
      data.append('user_id', userId);
      data.append('device_id', player.stars.playerId);
      data.append('os_version', browser.isOsx ? "macOS" : "windows");
      data.append('app_version', "0");
      data.append('content_id', player.stars.contentId);
      data.append('content_url', encodeURI(player.stars.currentUrl));
      data.append('play_type', "download");
      data.append('state', "normal");
      data.append('license', player.config.starplayer.app.license);
      data.append('date', getEventDateStyle(new Date()));
      data.append('online', "yes");
      //data.append('format', player.config.starplayer.responseType);

      fetchPolyfill(player.config.starplayer.eventUrl, {
        method: 'POST',
        headers: {
          'format': player.config.starplayer.responseType,
        },
        body: data
      }).then((response) => {
        return response.text()
      }).then((str) => {
        player.beginContentEvent(str);
      }).catch((e) => {
        player.debug.log(e);
      });
    }
  },

  ContentEvent(event, callback) {
    
    const player = this;
    if (player.stars.sentUnloadEvent == true) {
      return;
    }

    this.debug.log(`ContentEvent event: ${event}`);

    const userId = player.config.starplayer.userId;
    if (is.url(player.config.starplayer.eventUrl) && userId != 'ANONYMOUS') {
      let data = new FormData();
      data.append('event', event);
      data.append('user_id', userId);
      data.append('device_id', player.stars.playerId);
      data.append('os_version', browser.isOsx ? "macOS" : "windows");
      data.append('app_version', "0");
      data.append('content_id', player.stars.contentId);
      data.append('content_url', encodeURI(player.stars.currentUrl));
      data.append('play_type', "download");
      data.append('license', player.config.starplayer.app.license);
      let time = player.tracker.getPlayedTime();
      //let playedTime = time - player.stars.playedTime;
      //player.stars.playedTime = time;
      data.append('play_time', time);
      data.append('play_time2', time * 1000);
      let currentTick = new Date().getTime();
      if (player.stars.tick == 0) {
        player.stars.tick = currentTick;
      }
      let diff = currentTick - player.stars.tick;
      const maximumLimit = 300000;
      if (diff < 0 || diff > maximumLimit) {
        diff = 180000;
      }
      player.stars.tick = currentTick;
      data.append('latest_playtime', parseInt(diff / 1000, 10));
      data.append('latest_playtime2', diff);
      const speed = player.speed;
      data.append('latest_ratio_playtime', parseInt(diff * speed.toFixed(1) / 1000, 10));
      data.append('content_duration', parseInt(player.duration, 10));
      data.append('current_position', parseInt(player.currentTime, 10));

      data.append('date', getEventDateStyle(new Date()));
      data.append('online', "yes");
      data.append('play_ratio', speed.toFixed(1));
      //data.append('format', player.config.starplayer.responseType);

      data.append('tracker_time', parseInt(player.tracker.getTrackerTime(), 10));
      data.append('tracker_data', player.tracker.getTracker());

      if (event == 'end_content') {
        const result = navigator.sendBeacon(player.config.starplayer.eventUrl, data);
        if (result && is.function(callback)) {
          callback("");
        }
        player.stars.sentUnloadEvent = true;
      } else {
        fetchPolyfill(player.config.starplayer.eventUrl, {
          method: 'POST',
          headers: {
            'format': player.config.starplayer.responseType,
          },
          body: data
        }).then((response) => {
          return response.text()
        }).then((str) => {
          //player.playingContentEvent(str);
          if (is.function(callback)) {
            callback(str);
          }
        }).catch((e) => {
          player.debug.log(e);
        });
      }

    }
  },
};

export default starplayer;
